/**
 * useQuizLiveAdmin Hook
 * Admin controls for family admin to manage live quiz sessions
 * Used on mobile to control the quiz (start, next question, show rankings)
 */

import { useState, useCallback, useEffect, useRef } from "react";
import { supabase } from "@/lib/supabase/client";
import { useAuth } from "@/providers/AuthProvider";
import {
  LiveQuizSession,
  LiveQuizParticipant,
  Quiz,
  QuizQuestion,
  QuizSection,
  RankingEntry,
  QuestionResult,
  LiveQuizAnswer,
  TeamMemberOption,
  QuizNotificationConfig,
  QuestionPhase,
  ParticipantAnswerResult,
} from "../types";
import { getLottieByKey } from "@/lib/lottie-registry";

interface UseQuizLiveAdminReturn {
  // Session state
  session: LiveQuizSession | null;
  quiz: Quiz | null;
  participants: LiveQuizParticipant[];
  rankings: RankingEntry[];
  
  // Current state
  currentQuestion: QuizQuestion | null;
  currentSection: QuizSection | null;
  questionAnswers: LiveQuizAnswer[];
  allReady: boolean;
  
  // Question phase state (countdown, active, results)
  questionPhase: QuestionPhase;
  countdownSeconds: number;
  lastQuestionResult: QuestionResult | null;
  participantResults: ParticipantAnswerResult[];
  
  // Pause state
  isPaused: boolean;
  pausedForParticipant: LiveQuizParticipant | null;
  pauseTimeRemaining: number;
  disconnectedCaptains: LiveQuizParticipant[];

  /** Per-participant connection status derived from last_seen_at age */
  participantConnections: Record<string, "online" | "warning" | "offline">;

  // Answer reveal state
  answerRevealed: boolean;

  // UI state
  isLoading: boolean;
  isConnected: boolean;
  error: string | null;
  
  // Session management
  createSession: (quizId: string, notificationConfig?: QuizNotificationConfig) => Promise<string | null>;
  loadSession: (sessionId: string) => Promise<boolean>;
  
  // Quiz control
  startQuiz: () => Promise<boolean>;
  startCountdown: (questionIndex: number) => Promise<boolean>;
  nextQuestion: () => Promise<boolean>;
  showAnswer: () => Promise<boolean>;
  showResults: () => Promise<boolean>;
  showRanking: () => Promise<boolean>;
  startNextSection: () => Promise<boolean>;
  endQuiz: () => Promise<boolean>;
  cancelQuiz: () => Promise<boolean>;
  
  // Pause/Resume
  pauseQuiz: (participantId: string) => Promise<boolean>;
  resumeQuiz: () => Promise<boolean>;
  
  // Captain reassignment
  getTeamMembers: (teamId: string) => Promise<TeamMemberOption[]>;
  reassignCaptain: (participantId: string, newUserId: string) => Promise<boolean>;
  
  // Notifications
  sendQuizNotification: (config: QuizNotificationConfig) => Promise<boolean>;

  // Refresh
  refreshData: () => Promise<void>;
}

interface UseQuizLiveAdminOptions {
  /** Session ID to load */
  sessionId?: string;
}

/**
 * Hook for family admin to control live quiz
 */
export const useQuizLiveAdmin = (options: UseQuizLiveAdminOptions = {}): UseQuizLiveAdminReturn => {
  const { sessionId: initialSessionId } = options;
  const { activeProfile, activeWeekend, roles } = useAuth();
  
  // Refs
  const channelRef = useRef<ReturnType<typeof supabase.channel> | null>(null);
  
  // State
  const [session, setSession] = useState<LiveQuizSession | null>(null);
  const [quiz, setQuiz] = useState<Quiz | null>(null);
  const [participants, setParticipants] = useState<LiveQuizParticipant[]>([]);
  const [questionAnswers, setQuestionAnswers] = useState<LiveQuizAnswer[]>([]);
  const [pauseTimeRemaining, setPauseTimeRemaining] = useState(0);
  
  // Question phase state
  const [questionPhase, setQuestionPhase] = useState<QuestionPhase>("active");
  const [countdownSeconds, setCountdownSeconds] = useState(0);
  const [lastQuestionResult, setLastQuestionResult] = useState<QuestionResult | null>(null);
  const [participantResults, setParticipantResults] = useState<ParticipantAnswerResult[]>([]);
  
  const [isLoading, setIsLoading] = useState(false);
  const [isConnected, setIsConnected] = useState(false);
  const [error, setError] = useState<string | null>(null);
  
  // Refs for timers
  const pauseTimerRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const disconnectCheckRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const countdownTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const countdownIntervalRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const actuallyStartQuestionRef = useRef<((questionIndex: number) => Promise<void>) | null>(null);
  
  // Check if user is admin
  const isAdmin = roles.isFamilieAdmin || roles.isSuperAdmin;
  
  // Derived state
  const currentQuestion = session && quiz?.questions
    ? quiz.questions.find(q => q.id === session.questions_order[session.current_question_index]) || null
    : null;
  
  const currentSection = session && quiz?.sections
    ? quiz.sections[session.current_section_index] || null
    : null;
  
  const allReady = participants.length > 0 && participants.every(p => p.is_ready);
  
  // Pause state
  const isPaused = session?.status === "paused";
  
  const pausedForParticipant = session?.paused_for_participant_id
    ? participants.find(p => p.id === session.paused_for_participant_id) || null
    : null;
  
  // Find disconnected captains (last_seen > 5 seconds ago during playing)
  const disconnectedCaptains = participants.filter(p => {
    if (!p.last_seen_at || session?.status !== "playing") return false;
    const lastSeen = new Date(p.last_seen_at).getTime();
    const now = Date.now();
    return (now - lastSeen) > 5000;
  });

  // Per-participant connection status: < 4s online, 4-6s warning, > 6s offline
  const participantConnections: Record<string, "online" | "warning" | "offline"> = {};
  const connNow = Date.now();
  for (const p of participants) {
    if (!p.last_seen_at) {
      participantConnections[p.id] = "offline";
      continue;
    }
    const age = connNow - new Date(p.last_seen_at).getTime();
    if (age < 4000) participantConnections[p.id] = "online";
    else if (age < 6000) participantConnections[p.id] = "warning";
    else participantConnections[p.id] = "offline";
  }
  
  const rankings: RankingEntry[] = participants
    .sort((a, b) => b.total_score - a.total_score)
    .map((participant, index) => {
      const lottie = getLottieByKey(participant.mascot_key);
      return {
        rank: index + 1,
        participant,
        total_score: participant.total_score,
        correct_answers: participant.correct_answers,
        average_time_ms: 0,
        mascotLottie: lottie?.source ? lottie : undefined,
      };
    });
  
  /**
   * Fetch session data
   */
  const fetchSession = useCallback(async (sessionId: string): Promise<LiveQuizSession | null> => {
    const { data, error: fetchError } = await supabase
      .from("quiz_live_sessions")
      .select(`
        *,
        quiz:quizzes (
          *,
          sections:quiz_sections (*),
          questions:quiz_questions (*)
        )
      `)
      .eq("id", sessionId)
      .single();
    
    if (fetchError) {
      console.error("Error fetching session:", fetchError);
      return null;
    }
    
    return data as unknown as LiveQuizSession;
  }, []);
  
  /**
   * Fetch participants
   */
  const fetchParticipants = useCallback(async (sessionId: string): Promise<LiveQuizParticipant[]> => {
    const { data, error: fetchError } = await supabase
      .from("quiz_live_participants")
      .select(`
        *,
        team:teams (id, name, color, avatar_url),
        player:profiles!player_user_id (id, full_name, nickname, avatar_url)
      `)
      .eq("session_id", sessionId);
    
    if (fetchError) {
      console.error("Error fetching participants:", fetchError);
      return [];
    }
    
    return data as unknown as LiveQuizParticipant[];
  }, []);
  
  /**
   * Fetch answers for current question
   */
  const fetchQuestionAnswers = useCallback(async (sessionId: string, questionId: string): Promise<LiveQuizAnswer[]> => {
    const { data, error: fetchError } = await supabase
      .from("quiz_live_answers")
      .select("*")
      .eq("session_id", sessionId)
      .eq("question_id", questionId);
    
    if (fetchError) {
      console.error("Error fetching answers:", fetchError);
      return [];
    }
    
    return data as LiveQuizAnswer[];
  }, []);
  
  /**
   * Subscribe to realtime updates
   */
  const subscribeToSession = useCallback((sessionId: string) => {
    if (channelRef.current) {
      supabase.removeChannel(channelRef.current);
    }
    
    const channel = supabase.channel(`quiz_live_admin:${sessionId}`);

    // Session updates (so admin reflects pauses written by web display or other sources)
    channel.on(
      "postgres_changes",
      {
        event: "UPDATE",
        schema: "public",
        table: "quiz_live_sessions",
        filter: `id=eq.${sessionId}`,
      },
      async () => {
        const updatedSession = await fetchSession(sessionId);
        if (updatedSession) {
          setSession(updatedSession);
          setQuiz(updatedSession.quiz || null);
        }
      }
    );

    // Participant updates
    channel.on(
      "postgres_changes",
      {
        event: "*",
        schema: "public",
        table: "quiz_live_participants",
        filter: `session_id=eq.${sessionId}`,
      },
      async () => {
        const updatedParticipants = await fetchParticipants(sessionId);
        setParticipants(updatedParticipants);
      }
    );
    
    // Answer updates
    channel.on(
      "postgres_changes",
      {
        event: "INSERT",
        schema: "public",
        table: "quiz_live_answers",
        filter: `session_id=eq.${sessionId}`,
      },
      (payload) => {
        setQuestionAnswers(prev => [...prev, payload.new as LiveQuizAnswer]);
      }
    );
    
    channel.subscribe((status, err) => {
      if (status === "SUBSCRIBED") {
        setIsConnected(true);
      } else if (status === "CHANNEL_ERROR") {
        console.error("[QuizLiveAdmin] Channel error:", err);
      }
    });
    
    channelRef.current = channel;
  }, [fetchParticipants, fetchSession]);
  
  /**
   * Create new live session with optional notification config
   */
  const createSession = useCallback(async (quizId: string, notificationConfig?: QuizNotificationConfig): Promise<string | null> => {
    if (!isAdmin || !activeProfile?.id) {
      setError("Je moet familie admin zijn om een quiz te starten");
      return null;
    }
    
    setIsLoading(true);
    setError(null);
    
    try {
      // Fetch quiz with questions
      const { data: quizData, error: quizError } = await supabase
        .from("quizzes")
        .select(`
          *,
          sections:quiz_sections (*),
          questions:quiz_questions (*)
        `)
        .eq("id", quizId)
        .single();
      
      if (quizError) throw quizError;
      
      // Cast to our type
      const quizTyped = quizData as unknown as Quiz & { 
        sections?: QuizSection[]; 
        questions?: QuizQuestion[];
      };
      
      // Get all questions
      const allQuestions = quizTyped.questions || [];
      
      if (allQuestions.length === 0) {
        setError("Deze quiz heeft geen vragen");
        return null;
      }
      
      // Build questions order based on sections (if any) and order_index
      let questionsOrder: string[] = [];
      
      if (quizTyped.sections && quizTyped.sections.length > 0) {
        // Sort sections by order_index
        const sortedSections = [...quizTyped.sections].sort(
          (a, b) => (a.order_index || 0) - (b.order_index || 0)
        );
        
        // For each section, get questions in that section sorted by order_index
        for (const section of sortedSections) {
          const sectionQuestions = allQuestions
            .filter(q => q.section_id === section.id)
            .sort((a, b) => (a.order_index || 0) - (b.order_index || 0));
          questionsOrder.push(...sectionQuestions.map(q => q.id));
        }
        
        // Add any questions without a section at the end
        const unsectionedQuestions = allQuestions
          .filter(q => !q.section_id)
          .sort((a, b) => (a.order_index || 0) - (b.order_index || 0));
        questionsOrder.push(...unsectionedQuestions.map(q => q.id));
      } else {
        // No sections, just sort by order_index
        const sortedQuestions = [...allQuestions].sort(
          (a, b) => (a.order_index || 0) - (b.order_index || 0)
        );
        questionsOrder = sortedQuestions.map(q => q.id);
      }
      
      // Determine quiz master (host) - use selected from config or default to current user
      const quizMasterId = notificationConfig?.quiz_master?.user_id || activeProfile.id;
      
      // Create session (notification config stored separately if columns exist)
      const insertData: Record<string, unknown> = {
        quiz_id: quizId,
        weekend_id: activeWeekend?.id || null,
        host_user_id: quizMasterId,
        status: "lobby",
        questions_order: questionsOrder,
        total_questions: questionsOrder.length,
        timer_seconds: quizTyped.timer_seconds || 30,
      };
      
      // Try to add notification columns (may not exist in older schema)
      if (notificationConfig) {
        insertData.notification_title = notificationConfig.title;
        insertData.notification_lottie = notificationConfig.lottie_animation;
      }
      
      const { data: sessionData, error: sessionError } = await supabase
        .from("quiz_live_sessions")
        .insert(insertData)
        .select()
        .single();
      
      if (sessionError) {
        console.error("Session creation error:", sessionError);
        // If it's a column error, retry without notification columns
        if (sessionError.message?.includes("column") || sessionError.code === "42703") {
          const { data: retryData, error: retryError } = await supabase
            .from("quiz_live_sessions")
            .insert({
              quiz_id: quizId,
              weekend_id: activeWeekend?.id || null,
              host_user_id: quizMasterId,
              status: "lobby",
              questions_order: questionsOrder,
              total_questions: questionsOrder.length,
              timer_seconds: quizTyped.timer_seconds || 30,
            })
            .select()
            .single();
          
          if (retryError) throw retryError;
          
          setSession(retryData as unknown as LiveQuizSession);
          setQuiz(quizTyped as Quiz);
          subscribeToSession(retryData.id);
          return retryData.id;
        }
        throw sessionError;
      }
      
      setSession(sessionData as unknown as LiveQuizSession);
      setQuiz(quizTyped as Quiz);
      
      // Subscribe
      subscribeToSession(sessionData.id);
      
      return sessionData.id;
    } catch (err) {
      console.error("Error creating session:", err);
      setError("Fout bij aanmaken quiz sessie");
      return null;
    } finally {
      setIsLoading(false);
    }
  }, [isAdmin, activeProfile?.id, activeWeekend?.id, subscribeToSession]);
  
  /**
   * Load existing session
   */
  const loadSession = useCallback(async (sessionId: string): Promise<boolean> => {
    if (!isAdmin) {
      setError("Je moet familie admin zijn");
      return false;
    }
    
    setIsLoading(true);
    
    try {
      const sessionData = await fetchSession(sessionId);
      if (!sessionData) {
        setError("Quiz sessie niet gevonden");
        return false;
      }
      
      setSession(sessionData);
      setQuiz(sessionData.quiz || null);
      
      const participantsData = await fetchParticipants(sessionId);
      setParticipants(participantsData);
      
      subscribeToSession(sessionId);
      
      return true;
    } catch (err) {
      console.error("Error loading session:", err);
      return false;
    } finally {
      setIsLoading(false);
    }
  }, [isAdmin, fetchSession, fetchParticipants, subscribeToSession]);
  
  /**
   * Start countdown for a specific question (5-4-3-2-1 GO!)
   * Writes countdown_started_at to DB with a buffer so all clients receive the
   * postgres_changes event BEFORE the countdown actually starts. This guarantees
   * everyone sees the full 5-second countdown, even with realtime/network latency.
   *
   * Also flips status from "lobby" → "playing" so the web display transitions
   * out of the lobby view and shows the countdown alongside the players.
   */
  const startCountdown = useCallback(async (questionIndex: number): Promise<boolean> => {
    if (!session?.id) return false;

    const COUNTDOWN_SECONDS = 5;
    // Buffer for network + realtime delivery so countdown_started_at is in the
    // future when clients receive it. ~800ms is enough for fast networks.
    const COUNTDOWN_START_DELAY_MS = 800;
    const startTime = Date.now() + COUNTDOWN_START_DELAY_MS;
    const startTimeIso = new Date(startTime).toISOString();

    const { error } = await supabase
      .from("quiz_live_sessions")
      .update({
        status: "playing",
        current_phase: "countdown",
        countdown_started_at: startTimeIso,
        current_question_index: questionIndex,
        answer_revealed: false,
        results_shown_for_question_id: null,
        // Stamp started_at on the very first countdown so the quiz duration
        // reflects the moment the host started the quiz, not the first question.
        started_at: session.started_at || new Date().toISOString(),
      })
      .eq("id", session.id);

    if (error) {
      console.error("[QuizAdmin] Error starting countdown:", error);
      return false;
    }

    setQuestionPhase("countdown");
    setCountdownSeconds(COUNTDOWN_SECONDS);
    setLastQuestionResult(null);
    setParticipantResults([]);
    setQuestionAnswers([]);

    // Decrement countdown every 100ms, calculated against the same startTime
    // we wrote to the DB, so admin and clients stay in sync.
    if (countdownIntervalRef.current) clearInterval(countdownIntervalRef.current);
    countdownIntervalRef.current = setInterval(() => {
      const elapsed = Math.max(0, Math.floor((Date.now() - startTime) / 1000));
      const remaining = Math.max(0, COUNTDOWN_SECONDS - elapsed);
      setCountdownSeconds(remaining);
      if (remaining <= 0 && countdownIntervalRef.current) {
        clearInterval(countdownIntervalRef.current);
        countdownIntervalRef.current = null;
      }
    }, 100);

    // After countdown completes, start the question via ref (avoids stale closure)
    if (countdownTimerRef.current) clearTimeout(countdownTimerRef.current);
    countdownTimerRef.current = setTimeout(() => {
      actuallyStartQuestionRef.current?.(questionIndex);
    }, COUNTDOWN_START_DELAY_MS + COUNTDOWN_SECONDS * 1000);

    return true;
  }, [session?.id, session?.started_at]);

  /**
   * Actually start the question after countdown (internal function)
   */
  const actuallyStartQuestion = useCallback(async (questionIndex: number) => {
    if (!session?.id) return;

    // Clear countdown interval
    if (countdownIntervalRef.current) {
      clearInterval(countdownIntervalRef.current);
      countdownIntervalRef.current = null;
    }
    setCountdownSeconds(0);

    const now = new Date();
    const endsAt = new Date(now.getTime() + (session.timer_seconds * 1000));

    const { error: updateError } = await supabase
      .from("quiz_live_sessions")
      .update({
        status: "playing",
        current_phase: "active",
        countdown_started_at: null,
        current_question_index: questionIndex,
        question_started_at: now.toISOString(),
        question_ends_at: endsAt.toISOString(),
        started_at: session.started_at || now.toISOString(),
        results_shown_for_question_id: null,
        answer_revealed: false,
      })
      .eq("id", session.id);

    if (updateError) {
      console.error("[QuizAdmin] ❌ Error starting question:", updateError);
      return;
    }

    setQuestionPhase("active");

    await supabase
      .from("quiz_live_participants")
      .update({ status: "playing" })
      .eq("session_id", session.id);

    setQuestionAnswers([]);

    const updatedSession = await fetchSession(session.id);
    if (updatedSession) setSession(updatedSession);
  }, [session?.id, session?.timer_seconds, session?.started_at, fetchSession]);

  // Keep ref in sync to avoid stale closures in setTimeout
  useEffect(() => {
    actuallyStartQuestionRef.current = actuallyStartQuestion;
  }, [actuallyStartQuestion]);
  
  /**
   * Start the quiz (from lobby to playing) - shows countdown first
   */
  const startQuiz = useCallback(async (): Promise<boolean> => {
    if (!session?.id || !allReady) {
      setError("Niet alle teams zijn klaar");
      return false;
    }
    
    // Start with countdown for first question
    return startCountdown(0);
  }, [session, allReady, startCountdown]);
  
  /**
   * Reveal correct answer early — clients highlight the correct option.
   * Full scored results still come via showResults().
   */
  const showAnswer = useCallback(async (): Promise<boolean> => {
    if (!session?.id) return false;

    try {
      const { error } = await supabase
        .from("quiz_live_sessions")
        .update({ answer_revealed: true })
        .eq("id", session.id);

      if (error) throw error;

      setSession(prev => prev ? { ...prev, answer_revealed: true } : null);
      return true;
    } catch (err) {
      console.error("Error showing answer:", err);
      return false;
    }
  }, [session?.id]);
  
  /**
   * Show full results screen (after question ends or time runs out)
   * Calls the Edge Function to ensure consistency across all clients
   * The Edge Function broadcasts results to everyone
   */
  const showResults = useCallback(async (): Promise<boolean> => {
    if (!session?.id || !currentQuestion) return false;
    
    try {
      
      const anonKey = process.env.EXPO_PUBLIC_SUPABASE_ANON_KEY || process.env.EXPO_PUBLIC_SUPABASE_KEY || "";
      const response = await fetch(
        `${process.env.EXPO_PUBLIC_SUPABASE_URL}/functions/v1/quiz-show-results`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            "Authorization": `Bearer ${anonKey}`,
            "apikey": anonKey,
          },
          body: JSON.stringify({
            session_id: session.id,
            question_id: currentQuestion.id,
            force: true,
          }),
        }
      );
      
      const result = await response.json();
      
      if (result.success) {
        // Update local state with results from Edge Function
      setQuestionPhase("results");
        setLastQuestionResult(result.results);
        setParticipantResults(result.participant_results);
      
      // Refetch participants to update scores
      const updatedParticipants = await fetchParticipants(session.id);
      setParticipants(updatedParticipants);
      
      return true;
      } else if (result.already_shown) {
        // Still update local phase since results were already broadcast
        setQuestionPhase("results");
        return true;
      } else {
        console.error("[QuizAdmin] Edge Function error:", result.error);
        setError(result.error || "Kon resultaten niet tonen");
        return false;
      }
    } catch (err) {
      console.error("Error showing results:", err);
      setError("Kon resultaten niet tonen");
      return false;
    }
  }, [session?.id, currentQuestion, fetchParticipants]);
  
  /**
   * Show current ranking
   */
  const showRanking = useCallback(async (): Promise<boolean> => {
    if (!session?.id) return false;
    
    try {
      // Refetch participants for latest scores
      const updatedParticipants = await fetchParticipants(session.id);
      setParticipants(updatedParticipants);
      
      const currentRankings: RankingEntry[] = updatedParticipants
        .sort((a, b) => b.total_score - a.total_score)
        .map((participant, index) => {
          const lottie = getLottieByKey(participant.mascot_key);
          return {
            rank: index + 1,
            participant,
            total_score: participant.total_score,
            correct_answers: participant.correct_answers,
            average_time_ms: 0,
            mascotLottie: lottie?.source ? lottie : undefined,
          };
        });
      
      await supabase
        .from("quiz_live_sessions")
        .update({ status: "showing_ranking" })
        .eq("id", session.id);

      return true;
    } catch (err) {
      console.error("Error showing ranking:", err);
      return false;
    }
  }, [session?.id, fetchParticipants]);
  
  /**
   * Start next section
   */
  const startNextSection = useCallback(async (): Promise<boolean> => {
    if (!session?.id || !quiz?.sections) return false;
    
    const nextSectionIndex = session.current_section_index + 1;
    
    if (nextSectionIndex >= quiz.sections.length) {
      return endQuiz();
    }
    
    try {
      await supabase
        .from("quiz_live_sessions")
        .update({
          status: "section_break",
          current_section_index: nextSectionIndex,
        })
        .eq("id", session.id);

      return true;
    } catch (err) {
      console.error("Error starting next section:", err);
      return false;
    }
  }, [session?.id, quiz]);
  
  /**
   * End quiz
   */
  const endQuiz = useCallback(async (): Promise<boolean> => {
    if (!session?.id) return false;
    
    try {
      // Refetch participants for final scores
      const finalParticipants = await fetchParticipants(session.id);
      setParticipants(finalParticipants);
      
      const finalRankings: RankingEntry[] = finalParticipants
        .sort((a, b) => b.total_score - a.total_score)
        .map((participant, index) => {
          const lottie = getLottieByKey(participant.mascot_key);
          return {
            rank: index + 1,
            participant,
            total_score: participant.total_score,
            correct_answers: participant.correct_answers,
            average_time_ms: 0,
            mascotLottie: lottie?.source ? lottie : undefined,
          };
        });
      
      // Update session
      await supabase
        .from("quiz_live_sessions")
        .update({
          status: "completed",
          completed_at: new Date().toISOString(),
        })
        .eq("id", session.id);
      
      // Update participants status
      await supabase
        .from("quiz_live_participants")
        .update({ status: "finished" })
        .eq("session_id", session.id);
      
      // Award points to winning team (if weekend mode)
      if (session.weekend_id && finalRankings.length > 0) {
        const winner = finalRankings[0];
        if (winner.participant.team_id) {
          // Award team points based on placement
          // 1st: 100 pts, 2nd: 75 pts, 3rd: 50 pts, rest: 25 pts
          const pointsPerPlace = [100, 75, 50, 25];
          
          for (let i = 0; i < finalRankings.length; i++) {
            const ranking = finalRankings[i];
            const points = pointsPerPlace[i] || 25;
            
            // Add to score_history
            await supabase.rpc("award_team_points", {
              p_team_id: ranking.participant.team_id,
              p_points: points,
              p_reason: `Quiz: ${quiz?.name || "Live Quiz"} - ${ranking.rank}e plaats`,
              p_game_type: "quiz",
              p_awarded_by: activeProfile?.id,
              p_awarded_to: ranking.participant.player_user_id,
            });
          }
        }
      }
      
      return true;
    } catch (err) {
      console.error("Error ending quiz:", err);
      return false;
    }
  }, [session, quiz, activeProfile?.id, fetchParticipants]);
  
  /**
   * Move to next question (with countdown)
   */
  const nextQuestion = useCallback(async (): Promise<boolean> => {
    if (!session?.id) return false;
    
    const nextIndex = session.current_question_index + 1;
    
    if (nextIndex >= session.total_questions) {
      // Quiz finished
      return endQuiz();
    }
    
    // Start countdown for next question
    return startCountdown(nextIndex);
  }, [session, startCountdown, endQuiz]);
  
  /**
   * Cancel quiz
   */
  const cancelQuiz = useCallback(async (): Promise<boolean> => {
    if (!session?.id) return false;
    
    try {
      await supabase
        .from("quiz_live_sessions")
        .update({ status: "cancelled" })
        .eq("id", session.id);

      return true;
    } catch (err) {
      console.error("Error cancelling quiz:", err);
      return false;
    }
  }, [session?.id]);
  
  /**
   * Pause quiz (when captain disconnects)
   */
  const pauseQuiz = useCallback(async (participantId: string): Promise<boolean> => {
    if (!session?.id || session.status !== "playing") return false;
    
    const participant = participants.find(p => p.id === participantId);
    if (!participant) return false;
    
    try {
      // Calculate remaining time
      const now = Date.now();
      const endsAt = session.question_ends_at ? new Date(session.question_ends_at).getTime() : now;
      const remainingMs = Math.max(0, endsAt - now);
      
      // Conditional update: only pause if still playing (prevents race with web display)
      const { error: updateError, count } = await supabase
        .from("quiz_live_sessions")
        .update({
          status: "paused",
          paused_at: new Date().toISOString(),
          paused_for_participant_id: participantId,
          time_remaining_when_paused: Math.ceil(remainingMs / 1000),
        })
        .eq("id", session.id)
        .eq("status", "playing");
      
      if (updateError) throw updateError;
      // Another watcher already paused — nothing to do
      if (count === 0) return true;
      
      // Update participant status
      await supabase
        .from("quiz_live_participants")
        .update({ status: "disconnected" })
        .eq("id", participantId);
      
      // Start 2-minute countdown
      const pauseStart = Date.now();
      if (pauseTimerRef.current) clearInterval(pauseTimerRef.current);
      
      pauseTimerRef.current = setInterval(() => {
        const elapsed = Date.now() - pauseStart;
        const remaining = Math.max(0, 120 - Math.floor(elapsed / 1000));
        setPauseTimeRemaining(remaining);
        
        if (remaining <= 0 && pauseTimerRef.current) {
          clearInterval(pauseTimerRef.current);
        }
      }, 1000);
      
      setPauseTimeRemaining(120);
      
      // Refetch session
      const updatedSession = await fetchSession(session.id);
      if (updatedSession) setSession(updatedSession);
      
      return true;
    } catch (err) {
      console.error("Error pausing quiz:", err);
      return false;
    }
  }, [session, participants, fetchSession]);
  
  /**
   * Resume quiz (after captain reconnects)
   */
  const resumeQuiz = useCallback(async (): Promise<boolean> => {
    if (!session?.id || session.status !== "paused") return false;
    
    try {
      // Calculate new end time based on remaining time
      const remainingSeconds = session.time_remaining_when_paused || session.timer_seconds;
      const newEndsAt = new Date(Date.now() + (remainingSeconds * 1000));
      
      // Update session
      const { error: updateError } = await supabase
        .from("quiz_live_sessions")
        .update({
          status: "playing",
          current_phase: "active",
          paused_at: null,
          paused_for_participant_id: null,
          time_remaining_when_paused: null,
          question_ends_at: newEndsAt.toISOString(),
        })
        .eq("id", session.id);
      
      if (updateError) throw updateError;

      // Reset the paused participant back to "playing"
      if (session.paused_for_participant_id) {
        await supabase
          .from("quiz_live_participants")
          .update({ status: "playing" })
          .eq("id", session.paused_for_participant_id);
      }
      
      // Clear pause timer
      if (pauseTimerRef.current) {
        clearInterval(pauseTimerRef.current);
        pauseTimerRef.current = null;
      }
      setPauseTimeRemaining(0);
      
      const updatedSession = await fetchSession(session.id);
      if (updatedSession) setSession(updatedSession);

      return true;
    } catch (err) {
      console.error("Error resuming quiz:", err);
      return false;
    }
  }, [session, fetchSession]);
  
  /**
   * Get team members for captain reassignment
   */
  const getTeamMembers = useCallback(async (teamId: string): Promise<TeamMemberOption[]> => {
    const { data, error: fetchError } = await supabase
      .from("team_members")
      .select(`
        user_id,
        is_captain,
        profiles!user_id (
          full_name,
          nickname,
          avatar_url
        )
      `)
      .eq("team_id", teamId);
    
    if (fetchError) {
      console.error("Error fetching team members:", fetchError);
      return [];
    }
    
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    return (data || []).map((member: any) => {
      const profile = member.profiles;
      return {
        user_id: member.user_id,
        full_name: profile?.full_name || "Unknown",
        nickname: profile?.nickname || null,
        avatar_url: profile?.avatar_url || null,
        is_current_captain: member.is_captain,
      };
    });
  }, []);
  
  /**
   * Reassign captain to another team member
   */
  const reassignCaptain = useCallback(async (participantId: string, newUserId: string): Promise<boolean> => {
    if (!session?.id) return false;
    
    const participant = participants.find(p => p.id === participantId);
    if (!participant) return false;
    
    try {
      // Get new user's profile
      const { data: newUserProfile } = await supabase
        .from("profiles")
        .select("full_name, nickname")
        .eq("id", newUserId)
        .single();
      
      // Update participant with new player
      const { error: updateError } = await supabase
        .from("quiz_live_participants")
        .update({
          player_user_id: newUserId,
          status: "playing",
          last_seen_at: new Date().toISOString(),
        })
        .eq("id", participantId);
      
      if (updateError) throw updateError;
      
      // Resume quiz if it was paused for this participant
      if (session.paused_for_participant_id === participantId) {
        await resumeQuiz();
      }
      
      // Refetch participants
      const updatedParticipants = await fetchParticipants(session.id);
      setParticipants(updatedParticipants);
      
      return true;
    } catch (err) {
      console.error("Error reassigning captain:", err);
      return false;
    }
  }, [session, participants, resumeQuiz, fetchParticipants]);
  
  /**
   * Refresh session and participants data (for pull-to-refresh)
   */
  const refreshData = useCallback(async () => {
    if (!session?.id) return;
    
    const [updatedSession, updatedParticipants] = await Promise.all([
      fetchSession(session.id),
      fetchParticipants(session.id),
    ]);
    
    if (updatedSession) {
      setSession(updatedSession);
      setQuiz(updatedSession.quiz || null);
    }
    setParticipants(updatedParticipants);
  }, [session?.id, fetchSession, fetchParticipants]);
  
  /**
   * Send quiz start notification to all users
   */
  const sendQuizNotification = useCallback(async (config: QuizNotificationConfig): Promise<boolean> => {
    if (!session?.id || !activeProfile?.id) return false;
    
    try {
      // Get all user IDs (from all teams)
      const { data: allMembers, error: membersError } = await supabase
        .from("team_members")
        .select("user_id");
      
      if (membersError) throw membersError;
      
      const userIds = [...new Set((allMembers || []).map(m => m.user_id))];
      
      // Create in-app notifications for all users using correct "notifications" table
      const notifications = userIds.map(userId => ({
        user_id: userId,
        type: "game_challenge", // Required column
        title: config.title,
        body: `De ${quiz?.name || "Live Quiz"} begint nu! Doe snel mee.`,
        notification_type: "game_challenge",
        lottie_animation: config.lottie_animation,
        is_read: false,
        action_url: `/games/quiz/live?sessionId=${session.id}`,
        action_data: { 
          route: `/games/quiz/live?sessionId=${session.id}`,
          sessionId: session.id,
        },
      }));
      
      const { error: notifError } = await supabase
        .from("notifications")
        .insert(notifications);
      
      if (notifError) {
        console.error("Error creating in-app notifications:", notifError);
      }
      
      // Note: Push notifications are sent automatically via database trigger (on_notification_push)
      // The trigger calls the send-push-notification edge function for game_challenge notifications
      console.log(`[Quiz] Created ${notifications.length} notifications for live quiz ${session.id}`);
      
      return true;
    } catch (err) {
      console.error("Error sending quiz notification:", err);
      return false;
    }
  }, [session?.id, quiz?.name, activeProfile?.id]);
  
  // Auto-load session if provided
  useEffect(() => {
    if (initialSessionId && isAdmin) {
      loadSession(initialSessionId);
    }
  }, [initialSessionId, isAdmin]);
  
  // Track consecutive stale readings per participant to debounce on flaky 3G
  const staleCountRef = useRef<Record<string, number>>({});

  // Auto-detect disconnected captains and pause (2s poll, 5s threshold, debounce 2 cycles)
  useEffect(() => {
    if (session?.status !== "playing") {
      if (disconnectCheckRef.current) {
        clearInterval(disconnectCheckRef.current);
        disconnectCheckRef.current = null;
      }
      staleCountRef.current = {};
      return;
    }
    
    disconnectCheckRef.current = setInterval(async () => {
      const updatedParticipants = await fetchParticipants(session.id);
      setParticipants(updatedParticipants);
      
      const now = Date.now();
      for (const participant of updatedParticipants) {
        if (!participant.last_seen_at) continue;
        const lastSeen = new Date(participant.last_seen_at).getTime();
        const isStale = (now - lastSeen) > 5000;

        if (isStale && participant.status !== "disconnected") {
          staleCountRef.current[participant.id] = (staleCountRef.current[participant.id] || 0) + 1;
          // Only pause after 2 consecutive stale readings (debounce for poor networks)
          if (staleCountRef.current[participant.id] >= 2) {
            await pauseQuiz(participant.id);
            staleCountRef.current[participant.id] = 0;
            break;
          }
        } else {
          staleCountRef.current[participant.id] = 0;
        }
      }
    }, 2000);
    
    return () => {
      if (disconnectCheckRef.current) {
        clearInterval(disconnectCheckRef.current);
      }
    };
  }, [session?.status, session?.id, fetchParticipants, pauseQuiz]);
  
  // Track which question we've triggered auto-show for (prevent duplicates)
  const autoShowTriggeredRef = useRef<string | null>(null);
  
  // Reset auto-show trigger when question changes
  useEffect(() => {
    if (currentQuestion?.id) {
      autoShowTriggeredRef.current = null;
    }
  }, [currentQuestion?.id]);
  
  // Auto-show results when all participants have answered
  useEffect(() => {
    // Only check during active question phase
    if (
      session?.status !== "playing" || 
      questionPhase !== "active" ||
      participants.length === 0 ||
      !currentQuestion
    ) {
      return;
    }
    
    // Prevent duplicate triggers for same question
    if (autoShowTriggeredRef.current === currentQuestion.id) {
      return;
    }
    
    // Check if all participants have answered THIS question
    const answersForThisQuestion = questionAnswers.filter(a => a.question_id === currentQuestion.id);
    
    const allAnswered = participants.every(p => 
      answersForThisQuestion.some(a => a.participant_id === p.id)
    );
    
    if (allAnswered && answersForThisQuestion.length > 0) {
      // Mark as triggered before calling
      autoShowTriggeredRef.current = currentQuestion.id;
      
      // Small delay to ensure all answers are processed
      const timer = setTimeout(() => {
        showResults();
      }, 500);
      
      return () => clearTimeout(timer);
    }
  }, [session?.status, questionPhase, participants, questionAnswers, currentQuestion, showResults]);
  
  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (channelRef.current) {
        supabase.removeChannel(channelRef.current);
      }
      if (pauseTimerRef.current) {
        clearInterval(pauseTimerRef.current);
      }
      if (disconnectCheckRef.current) {
        clearInterval(disconnectCheckRef.current);
      }
      if (countdownTimerRef.current) {
        clearTimeout(countdownTimerRef.current);
      }
      if (countdownIntervalRef.current) {
        clearInterval(countdownIntervalRef.current);
      }
    };
  }, []);
  
  return {
    // Session state
    session,
    quiz,
    participants,
    rankings,
    
    // Current state
    currentQuestion,
    currentSection,
    questionAnswers,
    allReady,
    
    // Question phase state
    questionPhase,
    countdownSeconds,
    lastQuestionResult,
    participantResults,
    
    // Pause state
    isPaused,
    pausedForParticipant,
    pauseTimeRemaining,
    disconnectedCaptains,
    participantConnections,

    // Answer reveal state
    answerRevealed: session?.answer_revealed ?? false,

    // UI state
    isLoading,
    isConnected,
    error,

    // Session management
    createSession,
    loadSession,

    // Quiz control
    startQuiz,
    startCountdown,
    nextQuestion,
    showAnswer,
    showResults,
    showRanking,
    startNextSection,
    endQuiz,
    cancelQuiz,

    // Pause/Resume
    pauseQuiz,
    resumeQuiz,

    // Captain reassignment
    getTeamMembers,
    reassignCaptain,

    // Notifications
    sendQuizNotification,

    // Refresh
    refreshData,
  };
};

